import {
  faChartLine,
  faEnvelopeOpenText,
  faFileContract,
  faFileInvoiceDollar,
  faFilePdf,
  faHistory,
  faListUl,
  faSeedling,
  faShip,
} from '@fortawesome/free-solid-svg-icons';
import {
  faPalletAlt,
  faFunnelDollar,
  faDollyFlatbedAlt,
  faClipboardCheck,
  faCommentsDollar,
  faCogs,
  faCalendarAlt,
  faBuilding,
  faMapMarker,
  faContainerStorage,
  faFileAlt,
  faHandHoldingSeedling,
  faSack,
  faUserFriends,
  faWheat,
  faFarm,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';

import type { BaseMenuItemProps } from '@/components/menu';
import {
  PERMISSION_OBJ_ADDRESS,
  PERMISSION_OBJ_BOARDING,
  PERMISSION_OBJ_BV,
  PERMISSION_OBJ_CAMPAIGN,
  PERMISSION_OBJ_CONTAINER,
  PERMISSION_OBJ_COOPERATIVE,
  PERMISSION_OBJ_COUNTERPARTY,
  PERMISSION_OBJ_COUNTERPARTY_CONTRACT,
  PERMISSION_OBJ_CONTRACT_LINE_ITEM,
  PERMISSION_OBJ_CONSORTIUM,
  PERMISSION_OBJ_CV,
  PERMISSION_OBJ_CV_EXECUTION,
  PERMISSION_OBJ_DOCUMENT_IMPORT,
  PERMISSION_OBJ_FORMULA,
  PERMISSION_OBJ_LOT,
  PERMISSION_OBJ_PRODUCT,
  PERMISSION_OBJ_ROLE,
  PERMISSION_OBJ_TRANSFER,
  PERMISSION_OBJ_TRANSIT_ORDER,
  PERMISSION_READ,
  PERMISSION_OBJ_FARM,
  PERMISSION_OBJ_FARMER,
  PERMISSION_OBJ_ATTACHMENT,
  PERMISSION_OBJ_GLOBAL_DASHBOARD,
  PERMISSION_OBJ_WAYBILL,
} from '@/constants/permissions';

export interface IMenuItem extends BaseMenuItemProps {
  key: string;
  menuType?: string;
  parent: string;
  permissions: [string, number][];
  style?: any;
  descriptionMessageId?: string;
  sectionKey?: string;
}

export interface IAdminSection {
  key: string;
  messageId: string;
  icon?: React.ReactElement;
}

export const adminSections: IAdminSection[] = [
  // Icons already supported and could be used for the partners section
  // { key: "partners", messageId: "admin.section.partners", icon: <FontAwesomeIcon icon={faUserFriends} /> },
  { key: 'partners', messageId: 'admin.section.partners' },
  { key: 'catalog', messageId: 'admin.section.catalog' },
  { key: 'logistics', messageId: 'admin.section.logistics' },
  { key: 'audit', messageId: 'admin.section.audit' },
];

export const menuItems: IMenuItem[] = [
  {
    key: 'dashboard-global',
    state: 'base-layout.dashboard-global',
    icon: <FontAwesomeIcon icon={faChartLine} />,
    messageId: 'menu.dashboard-global',
    permissions: [[PERMISSION_OBJ_GLOBAL_DASHBOARD, PERMISSION_READ]],
    parent: 'root',
  },
  {
    key: 'waybills-group',
    state: 'base-layout.waybills',
    icon: <FontAwesomeIcon icon={faSack} />,
    messageId: 'menu.waybills',
    permissions: [[PERMISSION_OBJ_WAYBILL, PERMISSION_READ]],
    menuType: 'group',
    parent: 'root',
  },
  {
    key: 'waybills',
    state: 'base-layout.waybills',
    icon: <FontAwesomeIcon icon={faSack} />,
    messageId: 'menu.waybills',
    permissions: [[PERMISSION_OBJ_WAYBILL, PERMISSION_READ]],
    parent: 'waybills-group',
  },
  {
    key: 'waybill-imports',
    state: 'base-layout.waybill-imports',
    icon: <FontAwesomeIcon icon={faFilePdf} />,
    messageId: 'menu.waybill-imports',
    permissions: [[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_READ]],
    parent: 'waybills-group',
  },
  {
    key: 'lots-group',
    state: 'base-layout.lots',
    icon: <FontAwesomeIcon icon={faPalletAlt} />,
    messageId: 'menu.lots',
    permissions: [[PERMISSION_OBJ_LOT, PERMISSION_READ]],
    menuType: 'group',
    parent: 'root',
  },
  {
    key: 'lots',
    state: 'base-layout.lots',
    icon: <FontAwesomeIcon icon={faPalletAlt} />,
    messageId: 'menu.lots',
    permissions: [[PERMISSION_OBJ_LOT, PERMISSION_READ]],
    parent: 'lots-group',
  },
  {
    key: 'proposal',
    state: 'base-layout.proposal',
    icon: <FontAwesomeIcon icon={faFunnelDollar} />,
    messageId: 'menu.proposal',
    permissions: [[PERMISSION_OBJ_COUNTERPARTY_CONTRACT, PERMISSION_READ]],
    parent: 'lots-group',
  },
  {
    key: 'boardings-group',
    state: 'base-layout.boardings',
    icon: <FontAwesomeIcon icon={faListUl} />,
    messageId: 'menu.boardings',
    permissions: [[PERMISSION_OBJ_BOARDING, PERMISSION_READ]],
    menuType: 'group',
    parent: 'root',
  },
  {
    key: 'boardings',
    state: 'base-layout.boardings',
    icon: <FontAwesomeIcon icon={faListUl} />,
    messageId: 'menu.boardings',
    permissions: [[PERMISSION_OBJ_BOARDING, PERMISSION_READ]],
    parent: 'boardings-group',
  },
  {
    key: 'boarding-imports',
    state: 'base-layout.boarding-imports',
    icon: <FontAwesomeIcon icon={faEnvelopeOpenText} />,
    messageId: 'menu.boarding-imports',
    permissions: [[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_READ]],
    parent: 'boardings-group',
  },
  {
    key: 'transit-orders',
    state: 'base-layout.transit-orders',
    icon: <FontAwesomeIcon icon={faShip} />,
    messageId: 'menu.transit-orders',
    permissions: [[PERMISSION_OBJ_TRANSIT_ORDER, PERMISSION_READ]],
    parent: 'root',
  },
  {
    key: 'transfers',
    state: 'base-layout.transfers',
    icon: <FontAwesomeIcon icon={faDollyFlatbedAlt} />,
    messageId: 'menu.transfers',
    permissions: [[PERMISSION_OBJ_TRANSFER, PERMISSION_READ]],
    parent: 'lots-group',
  },
  {
    key: 'contracts',
    state: 'base-layout.counterparty-contracts',
    icon: <FontAwesomeIcon icon={faFileInvoiceDollar} />,
    messageId: 'menu.counterparty-contracts',
    permissions: [[PERMISSION_OBJ_COUNTERPARTY_CONTRACT, PERMISSION_READ]],
    menuType: 'group',
    parent: 'root',
  },
  {
    key: 'counterparty-contracts',
    state: 'base-layout.counterparty-contracts',
    icon: <FontAwesomeIcon icon={faFileInvoiceDollar} />,
    messageId: 'menu.counterparty-contracts',
    permissions: [[PERMISSION_OBJ_COUNTERPARTY_CONTRACT, PERMISSION_READ]],
    parent: 'contracts',
  },
  {
    key: 'contract-line-items',
    state: 'base-layout.contract-line-items',
    icon: <FontAwesomeIcon icon={faCommentsDollar} />,
    messageId: 'menu.contract-line-items',
    permissions: [[PERMISSION_OBJ_CONTRACT_LINE_ITEM, PERMISSION_READ]],
    parent: 'contracts',
  },
  {
    key: 'bvs',
    state: 'base-layout.bvs',
    icon: <FontAwesomeIcon icon={faClipboardCheck} />,
    messageId: 'menu.bvs',
    permissions: [[PERMISSION_OBJ_BV, PERMISSION_READ]],
    parent: 'lots-group',
  },
  {
    key: 'cvs-group',
    state: 'base-layout.cvs',
    icon: <FontAwesomeIcon icon={faFileContract} />,
    messageId: 'menu.cv-group',
    permissions: [[PERMISSION_OBJ_CV, PERMISSION_READ]],
    menuType: 'group',
    parent: 'root',
  },
  {
    key: 'cvs',
    state: 'base-layout.cvs',
    icon: <FontAwesomeIcon icon={faFileContract} />,
    messageId: 'menu.cvs',
    permissions: [[PERMISSION_OBJ_CV, PERMISSION_READ]],
    parent: 'cvs-group',
  },
  {
    key: 'cv-executions',
    state: 'base-layout.cv-executions',
    icon: <FontAwesomeIcon icon={faFileContract} />,
    messageId: 'menu.cv-executions',
    permissions: [[PERMISSION_OBJ_CV_EXECUTION, PERMISSION_READ]],
    parent: 'cvs-group',
  },
  {
    key: 'formulas',
    state: 'base-layout.formulas',
    icon: <FontAwesomeIcon icon={faFileAlt} />,
    messageId: 'menu.formulas',
    permissions: [[PERMISSION_OBJ_FORMULA, PERMISSION_READ]],
    parent: 'root',
  },

  // Admin section
  {
    key: 'admin',
    state: 'base-layout.admin',
    icon: <FontAwesomeIcon icon={faCogs} />,
    messageId: 'menu.admin',
    permissions: [[PERMISSION_OBJ_ROLE, PERMISSION_READ]],
    menuType: 'groupWithLanding',
    parent: 'root',
  },
  // Partners & counterparties section
  {
    key: 'consortiums',
    state: 'base-layout.consortiums',
    icon: <FontAwesomeIcon icon={faUserFriends} />,
    messageId: 'menu.consortiums',
    descriptionMessageId: 'menu.consortiums.description',
    permissions: [[PERMISSION_OBJ_CONSORTIUM, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'partners',
  },
  {
    key: 'cooperatives',
    state: 'base-layout.cooperatives',
    icon: <FontAwesomeIcon icon={faHandHoldingSeedling} />,
    messageId: 'menu.cooperatives',
    descriptionMessageId: 'menu.cooperatives.description',
    permissions: [[PERMISSION_OBJ_COOPERATIVE, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'partners',
  },
  {
    key: 'farmers',
    state: 'base-layout.farmers',
    icon: <FontAwesomeIcon icon={faWheat} />,
    messageId: 'menu.farmers',
    descriptionMessageId: 'menu.farmers.description',
    permissions: [
      [PERMISSION_OBJ_COUNTERPARTY, PERMISSION_READ],
      [PERMISSION_OBJ_FARMER, PERMISSION_READ],
    ],
    parent: 'admin',
    sectionKey: 'partners',
  },
  {
    key: 'farms',
    state: 'base-layout.farms',
    icon: <FontAwesomeIcon icon={faFarm} />,
    messageId: 'menu.farms',
    descriptionMessageId: 'menu.farms.description',
    permissions: [[PERMISSION_OBJ_FARM, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'partners',
  },
  // Catalog section
  {
    key: 'products',
    state: 'base-layout.products',
    icon: <FontAwesomeIcon icon={faSeedling} />,
    messageId: 'menu.products',
    descriptionMessageId: 'menu.products.description',
    permissions: [[PERMISSION_OBJ_PRODUCT, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'catalog',
  },
  {
    key: 'campaigns',
    state: 'base-layout.campaigns',
    icon: <FontAwesomeIcon icon={faCalendarAlt} />,
    messageId: 'menu.campaigns',
    descriptionMessageId: 'menu.campaigns.description',
    permissions: [[PERMISSION_OBJ_CAMPAIGN, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'catalog',
  },
  // Addresses & logistics section
  {
    key: 'addresses',
    state: 'base-layout.addresses',
    icon: <FontAwesomeIcon icon={faMapMarker} />,
    messageId: 'menu.addresses',
    descriptionMessageId: 'menu.addresses.description',
    permissions: [[PERMISSION_OBJ_ADDRESS, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'logistics',
  },
  {
    key: 'containers',
    state: 'base-layout.containers',
    icon: <FontAwesomeIcon icon={faContainerStorage} />,
    messageId: 'menu.containers',
    descriptionMessageId: 'menu.containers.description',
    permissions: [[PERMISSION_OBJ_CONTAINER, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'logistics',
  },
  {
    key: 'counterparties',
    state: 'base-layout.counterparties',
    icon: <FontAwesomeIcon icon={faBuilding} />,
    messageId: 'menu.counterparties',
    descriptionMessageId: 'menu.counterparties.description',
    permissions: [[PERMISSION_OBJ_COUNTERPARTY, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'logistics',
  },
  // Audit section
  {
    key: 'attachment-history',
    state: 'base-layout.attachment-history',
    icon: <FontAwesomeIcon icon={faHistory} />,
    messageId: 'menu.attachment-history',
    descriptionMessageId: 'menu.attachment-history.description',
    permissions: [[PERMISSION_OBJ_ATTACHMENT, PERMISSION_READ]],
    parent: 'admin',
    sectionKey: 'audit',
  },
];
